import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Container, ContainerContent } from '@/components/ui/Container'
import { ListRefreshIndicator } from '@/features/order-confirmation/components/ListRefreshIndicator'
import { useWorkflowSuppliers } from '@/features/order-confirmation/workflowSuppliersQueries'
import type { SupplierListItem } from '@/types/suppliers'

function EmailDomains({ domains }: { domains: string[] }) {
  if (domains.length === 0) {
    return <span className="text-sm italic text-gray-400">No email domains</span>
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {domains.map((domain) => (
        <span
          key={domain}
          className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600"
        >
          {domain}
        </span>
      ))}
    </div>
  )
}

function SettingsStatus({ hasSettings }: { hasSettings: boolean }) {
  return (
    <span className={`text-xs ${hasSettings ? 'text-gray-600' : 'italic text-gray-400'}`}>
      {hasSettings ? 'Custom settings' : 'Uses global settings'}
    </span>
  )
}

function SupplierRow({
  supplier,
  onOpenSettings,
}: {
  supplier: SupplierListItem
  onOpenSettings: (supplier: SupplierListItem) => void
}) {
  return (
    <tr className="border-t border-gray-100 transition-colors hover:bg-gray-50">
      <td className="px-4 py-3">
        <p className="font-medium text-gray-900">{supplier.name}</p>
        <p className="text-xs text-gray-400">{supplier.number || 'No number'}</p>
      </td>
      <td className="hidden md:table-cell px-4 py-3">
        <EmailDomains domains={supplier.email_domains} />
      </td>
      <td className="hidden sm:table-cell px-4 py-3">
        <SettingsStatus hasSettings={supplier.has_settings} />
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-500 hover:text-gray-900"
          aria-label={`Settings for ${supplier.name}`}
          onClick={() => onOpenSettings(supplier)}
        >
          <Settings className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  )
}

export function SuppliersPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const [createTarget, setCreateTarget] = useState<SupplierListItem | null>(null)

  const { data, isLoading, isFetching, isError, error } = useWorkflowSuppliers(workflowId)
  const isBackgroundRefreshing = isFetching && !isLoading && !!data

  const suppliers = data?.suppliers ?? []

  function openSettings(supplier: SupplierListItem) {
    if (supplier.has_settings) {
      navigate(`/workflow/${workflowId}/suppliers/${supplier.id}/settings`)
      return
    }
    setCreateTarget(supplier)
  }

  function confirmCreateSettings() {
    if (!createTarget || !workflowId) return
    navigate(`/workflow/${workflowId}/suppliers/${createTarget.id}/settings?create=1`)
    setCreateTarget(null)
  }

  if (!workflowId) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        No workflow selected.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-red-600">
        {error instanceof Error ? error.message : 'Could not load suppliers'}
      </div>
    )
  }

  return (
    <div className="container mx-auto">
      <Container>
        <ContainerContent className="py-6">
          <header className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Suppliers</h1>
          </header>

          <div className="relative">
            <ListRefreshIndicator visible={isBackgroundRefreshing} />
            <div className="data-sheet">
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 pb-2">Supplier</th>
                  <th className="hidden md:table-cell px-4 pb-2">Email domains</th>
                  <th className="hidden sm:table-cell px-4 pb-2">Settings</th>
                  <th className="px-4 pb-2 text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {suppliers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-16 text-center text-sm text-gray-400">
                      No suppliers found
                    </td>
                  </tr>
                ) : (
                  suppliers.map((supplier) => (
                    <SupplierRow
                      key={supplier.id}
                      supplier={supplier}
                      onOpenSettings={openSettings}
                    />
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        </ContainerContent>
      </Container>

      <Dialog.Root open={createTarget != null} onOpenChange={(open) => !open && setCreateTarget(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              Create supplier settings
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-500">
              {createTarget
                ? `Create custom settings for ${createTarget.name}? These will override the global settings for this supplier.`
                : null}
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateTarget(null)}>
                Cancel
              </Button>
              <Button type="button" onClick={confirmCreateSettings}>
                Create settings
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
